import {
  DEFAULT_ECONOMIC_LIFE,
  DEFAULT_ECONOMIC_LIFE_MANUFACTURED,
  DefaultEcoLifeField,
  EcoLife,
  SubjectiveCharacteristic,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { DEPRECIATION_SEGMENTS, DetailFeatures } from '../../constants.ts';
import { calculateDepreciationRatio } from '../../utils/calculateDepreciationPercentage.ts';
import { selectCompSequence, selectDepreciationSettings } from './orderSelectors.ts';
import {
  selectImprovementsById,
  selectImprovementsEntities,
} from './propertyDataSelectors/improvementsSelectors.ts';

const DEFAULT_FIELDS: [SubjectiveCharacteristic, EcoLife, DefaultEcoLifeField] = [
  'effectiveAge',
  'totalEconomicLife',
  'defaultEcoLife',
];

const getDepreciationFieldsForFeature = (
  feature?: DetailFeatures,
): [SubjectiveCharacteristic, EcoLife, DefaultEcoLifeField] => {
  if (isUndefined(feature)) {
    return DEFAULT_FIELDS;
  }

  const segment = DEPRECIATION_SEGMENTS.find((s) => s.orderFeature === feature);

  if (isUndefined(segment)) {
    return DEFAULT_FIELDS;
  }

  return [segment.effectiveAgeField, segment.ecoLifeField, segment.defaultEcoLifeField];
};

const getDefaultEconomicLife = (
  improvements: ReturnType<typeof selectImprovementsById>,
  depreciationSettings: ReturnType<typeof selectDepreciationSettings>,
  defaultEcoLifeField: DefaultEcoLifeField,
) => {
  // main dwelling for manufactured homes
  if (
    improvements?.propertyType === 'manufacturedHome' &&
    defaultEcoLifeField === 'defaultEcoLife'
  ) {
    return depreciationSettings.defaultEcoLifeManufactured ?? DEFAULT_ECONOMIC_LIFE_MANUFACTURED;
  }

  // everything else
  return depreciationSettings[defaultEcoLifeField] ?? DEFAULT_ECONOMIC_LIFE;
};

export const calculateDepreciationRatioForFeature = (
  depreciationSettings: ReturnType<typeof selectDepreciationSettings>,
  improvements: ReturnType<typeof selectImprovementsById>,
  feature?: DetailFeatures,
) => {
  if (isUndefined(improvements)) return undefined;

  const [effAgeField, ecoLifeField, defaultEcoLifeField] = depreciationSettings.depreciateSeparately
    ? getDepreciationFieldsForFeature(feature)
    : DEFAULT_FIELDS;

  const effectiveAge = improvements?.[effAgeField];
  const totalEconomicLife =
    improvements?.[ecoLifeField] ??
    getDefaultEconomicLife(improvements, depreciationSettings, defaultEcoLifeField);
  return calculateDepreciationRatio({ effectiveAge, totalEconomicLife });
};

export const selectSubjectDepreciationRatioForFeature = createSelector(
  [
    selectDepreciationSettings,
    (state) => selectImprovementsById(state, 'subject'),
    (state, feature?: DetailFeatures) => feature,
  ],
  (depreciationSettings, improvements, feature) =>
    calculateDepreciationRatioForFeature(depreciationSettings, improvements, feature),
);

export const selectDepreciationRatiosForFeature = createSelector(
  [
    selectDepreciationSettings,
    selectImprovementsEntities,
    selectCompSequence,
    (state, feature?: DetailFeatures) => feature,
  ],
  (depreciationSettings, improvements, compSequence, feature) => {
    if (isUndefined(improvements)) {
      return undefined;
    }

    const properties = ['subject', ...compSequence];

    return Object.fromEntries(
      properties.map((propID) => [
        propID,
        calculateDepreciationRatioForFeature(depreciationSettings, improvements[propID], feature),
      ]),
    );
  },
);

export const selectDepreciationRatiosForFeatureDisplay = createSelector(
  [selectDepreciationRatiosForFeature],
  (ratios) => {
    if (isUndefined(ratios)) return undefined;

    return Object.fromEntries(
      Object.entries(ratios).map(([propID, ratio]) => {
        if (isUndefined(ratio)) return [propID, undefined];

        return [propID, `${(ratio * 100).toFixed(2)}%`];
      }),
    );
  },
);
